import * as _ from "lodash-es";
import {
  nextTick,
  reactive,
  Ref,
  ref,
  UnwrapNestedRefs,
  watch,
  WatchStopHandle,
} from "vue";
import {
  NavigationGuardNext,
  NavigationGuardNextCallback,
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
  RouteLocationNormalized,
  RouteLocationNormalizedLoaded,
  RouteLocationRaw,
} from "vue-router";

import { hasSelectedViewRecord } from "@/components/graph/shortcuts";
import { deleteItem, selectAll, testShortcuts } from "@/components/shortcuts";

type Component = {
  previousRoute?: string | null;
  preventLeave?(
    to: RouteLocationNormalized,
    from: RouteLocationNormalized,
    next: NavigationGuardNext
  ): void;
  flush?(): void;
  initializeFocus?(): void;
};

type NavigationGuardNextArgument =
  | Error
  | RouteLocationRaw
  | boolean
  | NavigationGuardNextCallback
  | undefined;

export const useObject = <Value extends object>(
  _route: RouteLocationNormalizedLoaded,
  watchValue: (value: UnwrapNestedRefs<Value>) => Array<() => void>,
  options: {
    /**
     * Keep the current value (and thus the rendered object) while the route
     * params change and the next object loads, instead of resetting to the
     * loading state. Only meaningful for routes whose component is reused on
     * param changes (stable key in `routeComponentKey`), where resetting
     * would flash the whole screen's loading state on every switch.
     */
    preserveValueAcrossUpdates?: boolean;
  } = {}
): {
  /**
   * Bind the loaded object to the value management: `value` mirrors it, with
   * the save watchers (re)attached on every change. The created watcher is
   * owned by this instance and stopped by `release()`; a call arriving
   * *after* `release()` (a load resolving once the route already tore down)
   * is ignored.
   */
  setObjectValue: (
    originalValue: Readonly<Ref<Value | null | undefined>>
  ) => void;
  component: Ref<Component | undefined>;
  /**
   * Focuses the rendered component. The calling component must register it
   * itself with `onMounted(initializeComponentFocus)` and
   * `onUpdated(initializeComponentFocus)` in its setup, before the first
   * `await`.
   */
  initializeComponentFocus: () => void;
  /**
   * Register the navigation guards that flush the rendered component's
   * pending edits and reset the value across route transitions. The calling
   * component must call it in its setup, before the first `await`: the
   * guards attach to the active component instance (vue-router detaches
   * them when it unmounts).
   */
  installRouteCallbacks: () => void;
  /**
   * Stop the save watchers while keeping the current value rendered.
   *
   * For routes whose component is reused when switching objects (stable key
   * in `routeComponentKey`), call it when the object id changes: the re-bind
   * (`bindAsync`) releases the previous object's ref at that point, so a
   * deletion of that object would go unheard and the still-armed clone could
   * keep firing patches at a dead id until the next object loads — fatal
   * `no such page` in the controller (Sentry FRONTEND-D68). The next
   * `reset` re-arms the watchers, including when the reloaded object is
   * deep-equal to the rendered clone (switching back before anything
   * changed).
   */
  disarm: () => void;
  /**
   * Stop every watcher owned by this instance. The calling component must
   * register it on its releaser (or an unmount hook) in its setup, before
   * the first `await`.
   */
  release: () => void;
  reset: (originalValue: Value | null | undefined) => void;
  value: Ref<UnwrapNestedRefs<Value> | null | undefined>;
} => {
  let released = false;
  const objectValueWatchers: Array<WatchStopHandle> = [];
  const setObjectValue = (
    originalValue: Readonly<Ref<Value | null | undefined>>
  ) => {
    // A load can resolve after the route tore down (a fire-and-forget
    // `.then(setObjectValue)`, or setup resuming past an `await` on a
    // component Suspense discarded): binding then would recreate value and
    // save watchers that nobody stops anymore.
    if (released) return;
    objectValueWatchers.push(watch(originalValue, reset, { immediate: true }));
  };

  const component = ref<Component | undefined>();
  const flush = async () => {
    component.value?.flush?.();
    // Delay destruction so that watcher triggers. Vue provide zero
    // guarantees wrt this (and zero documentation on those behaviors ofc).
    await nextTick();
  };

  // Do note make the callback async in the onBeforeRoute hooks. It will confuse
  // the API since it can do both function and promise returns.
  //
  // After flush, reset internal state before going to the next route. Otherwise
  // very bad things happen, watchers stop triggering, and data stops saving in
  // the new page.
  const installRouteCallbacks = () => {
    onBeforeRouteUpdate((to, from, next) => {
      // Hash/query updates on the same path (e.g. `#block=...`) should not
      // reset the object value. Resetting here would clear `value` but the
      // source ref (bound to route params) wouldn't change, leaving the
      // component stuck in a loading state.
      if (to.path === from.path) {
        flush().then(() => next());
        return;
      }

      // When preserving, the previous object stays rendered until the new
      // one loads; `reset(newValue)` then swaps the value. Its save watchers
      // must not stay armed meanwhile: the params changed, so the source ref
      // no longer tracks the rendered object and its deletion would go
      // unheard (see `disarm`).
      if (options.preserveValueAcrossUpdates && to.name === from.name) {
        flush().then(() => {
          disarm();
          next();
        });
        return;
      }

      flush().then(() => {
        reset(undefined);
        next();
      });
    });
    onBeforeRouteLeave((to, from, next) => {
      flush().then(() => {
        if (component.value?.preventLeave) {
          const cb: NavigationGuardNext = (
            argument?: NavigationGuardNextArgument
          ) => {
            reset(undefined);
            if (argument === undefined) next();
            else if (argument instanceof Error) next(argument);
            else if (typeof argument === "boolean") next(argument);
            else if (typeof argument === "function") next(argument);
            else next(argument);
          };
          component.value.preventLeave(to, from, cb);
        } else {
          reset(undefined);
          next();
        }
      });
    });
  };
  const initializeComponentFocus = () => {
    component.value?.initializeFocus?.();
  };

  const stopComponentWatch = watch(component, (newComponent, oldComponent) => {
    if (oldComponent === undefined && newComponent != null) {
      newComponent.initializeFocus?.();
    }
  });

  const watchers = ref<Array<() => void>>([]);
  const startWatching = (value: UnwrapNestedRefs<Value>) => {
    watchers.value = watchValue(value);
  };
  const stopWatching = () => {
    _.each(watchers.value, (f) => f());
    watchers.value = [];
  };
  // Set by `disarm()`: the rendered clone is kept but its save watchers were
  // stopped, so the next `reset` must re-arm them even when the incoming
  // value is deep-equal to the clone (the isEqual guard below would
  // otherwise leave them off for good, silently dropping every later edit).
  let disarmed = false;
  const disarm = () => {
    disarmed = true;
    stopWatching();
  };
  const value: Ref<UnwrapNestedRefs<Value> | null | undefined> = ref();
  const reset = (originalValue: Value | null | undefined) => {
    if (originalValue !== undefined && originalValue !== null) {
      if (!_.isEqual(originalValue, value.value)) {
        stopWatching(); // Pragmatic way to avoid update cycle.
        const v = reactive(_.cloneDeep(originalValue));
        value.value = v;
        startWatching(v);
      } else if (
        disarmed &&
        value.value !== undefined &&
        value.value !== null
      ) {
        startWatching(value.value);
      }
    } else {
      stopWatching();
      value.value = originalValue;
    }
    disarmed = false;
  };
  const release = () => {
    released = true;
    stopComponentWatch();
    objectValueWatchers.forEach((stop) => stop());
    stopWatching();
  };
  return {
    component,
    disarm,
    initializeComponentFocus,
    installRouteCallbacks,
    release,
    reset,
    setObjectValue,
    value,
  };
};

// Isolate delete / select-all inside the object document so they do not
// hit the parent object's document-level hotkeys (TaskTopBar, …). Open,
// copy link, and other shortcuts are not isolated, which is why they
// still work on a selected view row. Skip isolation when a view record
// is selected so that row's `v-hotkey` bindings (including delete) can
// run.
export function keydown(event: KeyboardEvent) {
  if (!(testShortcuts(deleteItem, event) || testShortcuts(selectAll, event)))
    return;
  if (hasSelectedViewRecord()) return;
  event.stopPropagation();
}
